/**
 * Экран героя: фигура выбранного облика, уровень, опыт, статы, вехи (§07, §Т5).
 *
 * Статы показаны полосой и числом. Полоса — доля от значения на потолке уровня:
 * иначе игрок не понимает, много у него 120 здоровья или мало. Сами числа и
 * потолок приходят из `@hive/shared`, своих чисел здесь нет.
 */

import { formatTime, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { PlayerProfile } from "../api/profile.js";
import type { HeroAppearanceId } from "../game/appearance.js";
import {
  HERO_LEVEL_CAP,
  heroStatsView,
  heroUnlockLevelsReached,
  heroXpView,
  type HeroUnlockLevel,
} from "../game/hero.js";
import { AppearancePicker } from "./hero/AppearancePicker.js";
import { HeroFigure } from "./hero/HeroFigure.js";
import "./hero/hero.css";

export interface HeroScreenProps {
  readonly locale: LocaleCode;
  readonly timeZone: string;
  readonly profile: PlayerProfile;
  /**
   * Облик живёт в корневом компоненте: его же показывает шапка профиля, и при
   * локальном состоянии экрана шапка оставалась бы со старым аватаром.
   */
  readonly appearance: HeroAppearanceId;
  readonly onAppearanceChange: (next: HeroAppearanceId) => void;
}

/** Доля от потолка, обрезанная так, чтобы полоса первого уровня была видна. */
function share(value: number, cap: number): number {
  if (cap <= 0) return 1;
  return Math.min(1, Math.max(0.06, value / cap));
}

export function HeroScreen({
  locale,
  timeZone,
  profile,
  appearance,
  onAppearanceChange,
}: HeroScreenProps): React.JSX.Element {
  const { t } = useTranslation(["hero", "common"]);

  const stats = heroStatsView(profile.level);
  const peak = heroStatsView(HERO_LEVEL_CAP);
  const xp = heroXpView(profile.level, profile.xp);
  const unlocks = heroUnlockLevelsReached(profile.level);
  const shieldActive =
    profile.shieldUntil !== null && new Date(profile.shieldUntil).getTime() > Date.now();

  const tiles = [
    {
      key: "health",
      kind: "hero-card__bar--health",
      label: t("hero:health"),
      value: stats.healthMax,
      hint: t("hero:health.hint"),
      fill: share(stats.healthMax, peak.healthMax),
    },
    {
      key: "mana",
      kind: "hero-card__bar--mana",
      label: t("hero:mana"),
      value: stats.manaMax,
      hint: t("hero:mana.hint"),
      fill: share(stats.manaMax, peak.manaMax),
    },
    {
      key: "attack",
      kind: "hero-card__bar--attack",
      label: t("hero:attack"),
      value: stats.attackPower,
      hint: t("hero:attack.hint"),
      fill: share(stats.attackPower, peak.attackPower),
    },
    {
      key: "slots",
      kind: "hero-card__bar--slots",
      label: t("hero:panel_slots"),
      value: stats.panelSlots,
      hint: t("hero:panel_slots.hint"),
      fill: share(stats.panelSlots, peak.panelSlots),
    },
  ];

  return (
    <div className="screen screen--hero">
      <header className="screen__head">
        <h1 className="screen__title">{t("hero:title")}</h1>
      </header>

      <section className="hero-card anim-rise" aria-label={t("hero:title")}>
        <div className="hero-card__stage">
          <HeroFigure
            appearanceId={appearance}
            nickname={profile.nickname}
            level={profile.level}
          />

          <div className="hero-card__side">
            <div className="hero-card__identity">
              {/*
                i18n-ignore-start: ник игрока — данные, а не надпись интерфейса
              */}
              <p className="hero-card__nickname">{profile.nickname}</p>
              {/* i18n-ignore-end */}
              <p className="hero-card__level">
                {xp.atCap ? t("hero:level_max") : t("hero:level", { level: profile.level })}
              </p>
            </div>

            <ul className="hero-card__bars">
              {tiles.map((tile) => (
                <li key={tile.key} className={`hero-card__bar ${tile.kind}`}>
                  <span className="hero-card__bar-label">{tile.label}</span>
                  <span className="hero-card__bar-value">{tile.value}</span>
                  <span className="hero-card__bar-track">
                    <i
                      className="hero-card__bar-fill"
                      style={{ transform: `scaleX(${tile.fill})` }}
                    />
                  </span>
                  <span className="hero-card__bar-hint">{tile.hint}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="hero-card__xp">
          <div className="hero-card__xp-labels">
            <span>{t("hero:xp")}</span>
            {!xp.atCap && (
              <span className="hero-card__xp-numbers">
                {t("hero:xp_progress", {
                  current: xp.current - xp.levelStart,
                  next: xp.nextLevel - xp.levelStart,
                })}
              </span>
            )}
          </div>
          <div
            className="hero-card__xp-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(xp.progress * 100)}
          >
            <span className="hero-card__xp-fill" style={{ width: `${xp.progress * 100}%` }} />
          </div>
        </div>

        <div className="hero-card__notes">
          {shieldActive && profile.shieldUntil !== null && (
            <p className="hero-card__shield">
              {t("hero:shield_active", {
                time: formatTime(locale, new Date(profile.shieldUntil), timeZone),
              })}
            </p>
          )}
          <p className="hero-card__hearth">
            {profile.hearthHex !== null ? t("hero:hearth_set") : t("hero:hearth_missing")}
          </p>
        </div>

        <AppearancePicker value={appearance} onChange={onAppearanceChange} />
      </section>

      <section className="hero-milestones" aria-labelledby="hero-milestones-title">
        <h2 id="hero-milestones-title" className="screen__section-title">
          {t("hero:milestones")}
        </h2>
        <ul className="hero-milestones__list anim-stagger">
          {unlocks.map((level: HeroUnlockLevel, index: number) => (
            <li
              key={level}
              className="hero-milestones__item"
              style={{ "--i": index } as React.CSSProperties}
            >
              <span className="hero-milestones__level">{level}</span>
              <span className="hero-milestones__text">{t(`hero:unlock.${level}`)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
